import { OnClickFunctionType } from '@/types/common-dom/types';
import { SearchOutline } from '@weee-fe/kunlunicon';
import { useCallback, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { twMerge } from 'tailwind-merge';
import { WeeeIcon } from '@/components/common/Icon';

type ComponentProps = {
  keyword?: string;
  placeholder?: string;
  inputAutoFocus?: boolean;
  clickSetsSelectionRange?: boolean;
  onClick?: OnClickFunctionType;
  handleChange?: any;
  handleFocus?: any;
  handleKeyDown?: any;
  handleClearKeyword?: any;
};

const SEARCH_INPUT_DOM_ID = 'searchElId';

const SearchInputStaticMask = (props: ComponentProps) => {
  const { keyword, placeholder, clickSetsSelectionRange, handleClearKeyword, onClick } = props;

  const { t, lang } = useTranslation('common');

  const fakeSearchInputRef = useRef(null);
  const realInputRef = useRef(null);

  const activeFeedback =
    'transition-shadow duration-600 ease-out active:shadow-[0_0_0_1px] active:shadow-primary-1 focus-within:shadow-[0_0_0_1px] focus-within:shadow-primary-1';

  const placeholderTxt = placeholder || t('search_placeholder');

  const handleClick = useCallback(
    e => {
      if (clickSetsSelectionRange === true && realInputRef?.current?.value) {
        realInputRef.current.setSelectionRange?.(0, realInputRef.current.value.length);
      }
      onClick?.(e);
    },
    [clickSetsSelectionRange, onClick]
  );

  const handleClearInput = useCallback(
    e => {
      handleClearKeyword?.(e);
      realInputRef?.current?.focus?.();
    },
    [handleClearKeyword]
  );

  const handleKeyDown = useCallback(
    e => {
      props?.handleKeyDown?.(e);
      if (e.key === 'Enter') {
        if (realInputRef?.current?.blur && document.activeElement === realInputRef.current) {
          realInputRef.current.blur();
        }
      }
    },
    [props?.handleKeyDown]
  );

  return (
    <div
      ref={fakeSearchInputRef}
      onClick={handleClick}
      className={twMerge(
        'flex py-100 px-200 items-center flex-[1_0_0] self-stretch rounded-full bg-surface-200-bg',
        activeFeedback
      )}>
      <label htmlFor={SEARCH_INPUT_DOM_ID} className="w-full">
        <span className="sr-only">{keyword || placeholderTxt}</span>
        <div className="flex gap-100 items-center w-full">
          <div className="flex items-center p-100">
            <SearchOutline width={12} height={12} className="text-surface-100-fg-minor" />
          </div>
          <span className="relative w-full enki-body-xs text-input-100-fg-placeholder">
            <span className="block max-w-[120px] overflow-hidden whitespace-nowrap leading-[1.6rem]">
              {placeholderTxt}
            </span>
            <input
              data-testid="wid-search-fouced"
              type="text"
              ref={realInputRef}
              autoFocus={props?.inputAutoFocus}
              id={SEARCH_INPUT_DOM_ID}
              className="absolute w-full h-full left-0 right-[-2px] bottom-0 top-0 bg-surface-200-bg pr-500 box-border"
              placeholder={placeholderTxt}
              arial-label={placeholderTxt}
              autoComplete="off"
              onChange={props?.handleChange}
              onFocus={props?.handleFocus}
              onKeyDown={handleKeyDown}
              value={keyword}
              maxLength={80}
            />
            {keyword && (
              <div
                className="absolute box-border m-auto -right-[8px] top-0 bottom-0 w-[30px] h-[30px] flex items-center justify-center"
                onClick={handleClearInput}>
                <WeeeIcon
                  type="iconTimes"
                  className="text-surface-100-bg !text-[14px] bg-surface-100-fg-minor rounded-full w-[14px] h-[14px] flex items-center justify-center"
                />
              </div>
            )}
          </span>
        </div>
      </label>
    </div>
  );
};

export default SearchInputStaticMask;
